import { dayMs } from "verdi-common/timeConstants";
import { sleep } from "verdi-common/utils/timeoutUtils";
import * as VerdiWebClient from "verdi-web-client";
import { MasterIndex, Schedule } from "verdi-web-client";
import { doTick, SingleZoneCategory } from "verdi-web-client/Models";
import { IrrigationDeviceBase } from "verdi-web-client/Models/Devices/IrrigationDeviceBase";
import { getCardTypeFromEntity } from "@/features/cards/shared/card-routing/card-types";
import { useStore } from "@/store/store";
import { getCenterOfPolygon } from "../../utils/get-center-of-polygon";
import GlobalOptions from "../../utils/global-options";
import { URLParams } from "../../utils/url-params";
import FocusContext from "../map-management/focus-context";
import { getStartingPositionOfMap, MapLoadedPromise, setStartingPositionOfMap } from "../map-management/map-helper";
import MapEntityBase from "../map-management/map-entities/map-entity-base";
import { panTo, panToCoords } from "../map-management/panning/pan-to";

export function getIDOfInitialField(): string | undefined {
    const fieldParam = URLParams.getParam("field");
    if (fieldParam) {
        return fieldParam;
    }

    // TODO(jeff): Remove string type check once `GlobalOptions.defaultStartingField` goes back to just being `string | undefined`; see: <https://github.com/Verdi-Agriculture/verdi-monorepo/pull/359#discussion_r2452973737>
    if (typeof GlobalOptions.defaultStartingField === "string") {
        return GlobalOptions.defaultStartingField;
    }

    return undefined;
}

export function getIDOfInitialDevice(): string | undefined {
    return URLParams.getParam("dev");
}

export async function findDeviceByIDEventually(deviceID: string): Promise<IrrigationDeviceBase | undefined> {
    const collectionsToSearchForDeviceIn = [
        "blockValve",
        "irrigationDevice",
        "thirdPartyDevice",
        "SensoterraMoisture",
    ] as const;

    // TS really hates indexing the master index with strings. But I'm doing it anyway. So it means a lot of ts-ignores
    return Promise.race(
        collectionsToSearchForDeviceIn.map((collectionToSearch) =>
            (
                VerdiWebClient.MasterIndex[collectionToSearch] as unknown as SingleZoneCategory<
                    // @ts-ignore
                    ThirdPartyDeviceBase | IrrigationDevice | BlockValve
                >
            )
                .byIDeventually(deviceID)
                .catch(() => {
                    console.warn("couldn't find device specified in device parameter");
                    return undefined;
                }),
        ),
    );
}

/**
 * Pans the map to wherever it should start. This is based on URL paramaters like dev and field
 * as well as the last field the user selected (stored in "lastSelectedFieldIDByUserID")
 * @return {Promise<void>}
 */
export async function doInitialMapPan() {
    // identify what devices or fields need panning to based on url parameters and local storage
    const idOfDeviceToPanTo = getIDOfInitialDevice();
    const idOfFieldToPanTo = getIDOfInitialField();

    // if we are going to pan to a device, ignore the field and just go to the device
    if (idOfDeviceToPanTo) {
        // will be undefined if the search takes too long
        console.debug(`Panning to device specified in URL parameter: ${idOfDeviceToPanTo}`);
        return panToDeviceByID(idOfDeviceToPanTo).catch((e) => {
            console.warn(`Error with panning to selected device ${idOfDeviceToPanTo}`, e);
        });
    }
    // if we are going to pan to a field we should do that
    if (idOfFieldToPanTo) {
        console.debug(`Panning to field specified in URL parameter: ${idOfFieldToPanTo}`);
        return panToFieldByID(idOfFieldToPanTo).catch((e) => {
            console.warn(`Error with panning to selected field ${idOfFieldToPanTo}`, e);
        });
    }

    // if nothing above, just show the whole account
    return doFallbackMapPan();
}

export async function doFallbackMapPan() {
    console.debug("Panning to default map location");
    await MapLoadedPromise;
    FocusContext.setAOI(undefined);

    // if there are no fields on the account, use the default position.
    // @ts-ignore
    if (MasterIndex.aoi.all.length === 0) {
        const [lat, lng] = getStartingPositionOfMap();
        panToCoords(lat, lng);
    }
}
/**
 * Pan to the field based on the id. Handles making sure map is loaded and everything so this happens smoothly
 * @param idOfFieldToPanTo
 * @return {Promise<void>}
 */
async function panToFieldByID(idOfFieldToPanTo: string) {
    VerdiWebClient.MasterIndex.aoi
        .byIDeventually(idOfFieldToPanTo)
        .then(async (newActiveAOI) => {
            // preset the location we are going to go to so that if the map initializes, it uses this
            const center = getCenterOfPolygon(newActiveAOI.polygon);
            setStartingPositionOfMap(center[0], center[1]);

            // Actually wait for the map to load and set the AOI
            if (newActiveAOI) {
                setTimeout(async () => {
                    try {
                        await MapLoadedPromise;
                        FocusContext.setAOI(newActiveAOI);
                    } catch (e) {
                        console.warn("couldn't set aoi, not loaded");
                    }
                }, 100);
            }
        })
        .catch(async () => doFallbackMapPan());
}

/**
 * Pan to a device based on the id. Handles making sure map is loaded and everything so that this happens smoothly
 * @param idOfDeviceToPanTo
 * @return {Promise<void>}
 */
async function panToDeviceByID(idOfDeviceToPanTo: string) {
    // deviceModelToPanTo will be undefined if the search takes too long
    // we use a race so that, if it takes longer than a certain amount of time to load the device, it just load the default view while it waits
    const deviceModelToPanTo = await Promise.race([
        findDeviceByIDEventually(idOfDeviceToPanTo).then(async (newActiveDevice) => {
            if (newActiveDevice) {
                // preset the location we are going to go to so that if the map initializes, it uses this
                setStartingPositionOfMap(newActiveDevice.lat, newActiveDevice.lng);

                console.info("found matching device", newActiveDevice);

                // wait until this device is on the map and there is a leaflet element
                let attempts = 0;
                const maxAttempts = 40;
                while (
                    attempts < maxAttempts &&
                    !FocusContext.MapEntitesByModelID[newActiveDevice.id] &&
                    FocusContext.MapEntitesByModelID?.[newActiveDevice.id]?.onMap &&
                    FocusContext.MapEntitesByModelID?.[newActiveDevice.id]?.leafletElement
                ) {
                    await sleep(10 + attempts * attempts);
                    attempts++;
                }
                if (attempts >= maxAttempts) {
                    throw new Error(
                        `Error, could not find device map entity for id ${newActiveDevice.id}. It failed to load`,
                    );
                }

                const mapEntityOfDevice: MapEntityBase = FocusContext.MapEntitesByModelID[newActiveDevice.id];
                console.info("found Map entity for device", mapEntityOfDevice);
                // wait for the map to load
                await MapLoadedPromise;

                // spawn the info card
                useStore.getState().openCard({
                    entity: mapEntityOfDevice,
                    cardType: getCardTypeFromEntity(mapEntityOfDevice),
                });

                // without this, this fails sometimes
                await sleep(50);

                try {
                    panTo(mapEntityOfDevice);
                } catch (e) {
                    console.error("Error panning to device", e);
                    doFallbackMapPan();
                }
                if (newActiveDevice.linkedAreaOfInterest) {
                    FocusContext.setAOI(newActiveDevice.linkedAreaOfInterest, {
                        panToAOI: false,
                        panShouldAvoidInfoCardY: false,
                        panShouldAvoidInfoCardX: false,
                        padRatio: 0,
                    });
                }
            }
            return newActiveDevice;
        }),
        sleep(600),
    ]);
    // if the sleep promise returned in the race condition instead of the device one, then just pan the map out until the
    // device is found.
    if (!deviceModelToPanTo) {
        doFallbackMapPan();
    }
}

export async function doInitialScheduleLoad() {
    // run asynchronously because it's heavy and doesn't need to be loaded right away
    await sleep(300);
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const defaultSchedule = new Schedule(
        new Date(Date.now() - 20 * dayMs),
        new Date(Date.now() + 30 * dayMs),
        () => true,
    );

    await sleep(200);
    doTick();
    await sleep(200);
    doTick();
}
